/*  replo-free.js — the behaviour the Replo app's runtime used to provide on the Retinew PDP.
 *
 *  Replaces https://replocdn.com/w/<project>/<page>.min.js. The Replo page markup was
 *  already server-rendered into this theme; the only things the hosted bundle did were:
 *    1. collapsible  (the ingredient blocks and the FAQ)
 *    2. carousel     (the gallery and the testimonial rail)
 *    3. variant pick + add to cart (the three bundle cards)
 *  All three are re-implemented here so the page owes the Replo subscription nothing.
 *
 *  Build-time hooks added by intsuper-seo/docs/theme/retinew-native/build.py:
 *    [data-rf-open]                      collapsible that starts open (matches the old page)
 *    [data-rf-variant="<id>"]            bundle card -> Shopify variant id
 *    [data-rf-sel-class="<class>"]       the class Replo added to that card while selected
 *    [data-rf-preselect]                 the card selected on load
 */
(function () {
  'use strict';

  /* ---------- collapsible -------------------------------------------------- */
  function initCollapsibles(root) {
    root.querySelectorAll('[data-replo-collapsible]').forEach(function (c) {
      if (c.dataset.rfReady) return;
      c.dataset.rfReady = '1';

      var trig = c.querySelector('[aria-expanded]');
      if (!trig || trig.closest('[data-replo-collapsible]') !== c) return;
      var id = trig.getAttribute('aria-controls');
      var body = (id && document.getElementById(id)) || trig.nextElementSibling;
      if (!body) return;

      var open = c.hasAttribute('data-rf-open');
      function set(o) {
        open = o;
        c.setAttribute('data-state', o ? 'open' : 'closed');
        trig.setAttribute('aria-expanded', o ? 'true' : 'false');
        body.hidden = !o;
      }
      set(open);

      trig.addEventListener('click', function (e) {
        e.preventDefault();
        // Replo's collapsibles behaved as an accordion: opening one closed its siblings.
        if (!open && c.parentElement) {
          Array.prototype.forEach.call(c.parentElement.children, function (sib) {
            if (sib === c || !sib.hasAttribute || !sib.hasAttribute('data-replo-collapsible')) return;
            if (sib.getAttribute('data-state') !== 'open') return;
            var t = sib.querySelector('[aria-expanded]');
            if (t) t.click();
          });
        }
        set(!open);
      });
      trig.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); trig.click(); }
      });
    });
  }

  /* ---------- carousel ----------------------------------------------------- */
  function initCarousels(root) {
    root.querySelectorAll('[data-replo-carousel]').forEach(function (car) {
      if (car.dataset.rfReady) return;
      car.dataset.rfReady = '1';

      var track = car.querySelector('[data-replo-part="slide-track"]');
      if (!track) return;
      var slides = [].slice.call(track.querySelectorAll('[data-replo-part="slide"]'));

      var before = slides.length;

      // Replo shipped four UGC cards whose <video> never got a source — blank tiles with a
      // play button, on the original page too. Drop them rather than pad the rail with holes.
      slides = slides.filter(function (s) {
        var v = s.querySelector('video');
        if (v && !v.getAttribute('src') && !v.querySelector('source')) { s.remove(); return false; }
        return true;
      });
      // The dot row is one element per slide and Replo's runtime kept it in step. Find it by
      // shape (one small child per original slide), drop the dots whose slide just went, and
      // hide the row entirely when the whole rail is visible at once.
      var dots = null, dotOn = '';
      [].slice.call(car.querySelectorAll('div')).some(function (d) {
        if (d.children.length !== before) return false;
        var k = d.firstElementChild.getBoundingClientRect();
        if (k.height > 24 || k.height < 3) return false;
        dots = d;
        var cls = d.firstElementChild.className.trim().split(/\s+/);
        dotOn = cls.length > 1 ? cls[1] : '';
        return true;
      });
      if (dots) {
        while (dots.children.length > slides.length) dots.removeChild(dots.lastElementChild);
      }

      var prev = car.querySelector('[data-replo-part="nav-button"][data-direction="previous"]');
      var next = car.querySelector('[data-replo-part="nav-button"][data-direction="next"]');

      // Both buttons ship disabled in the server HTML, each carrying its own extra
      // "disabled" class. Read it once, before we touch anything.
      var prevOff = prev && prev.className.trim().split(/\s+/)[1];
      var nextOff = next && next.className.trim().split(/\s+/)[1];

      // Replo's runtime set the per-breakpoint slide count; build.py tags the one rail that
      // is not 1-up on mobile.
      function applyPerPage() {
        var m = track.getAttribute('data-rf-spp-mobile');
        if (m) track.style.setProperty('--replo-carousel-slides-per-page',
          window.matchMedia('(max-width: 640px)').matches ? m : '1');
        if (m) slides.forEach(function (s) { s.dataset.rfHadWidth = '0'; });
      }
      applyPerPage();

      // A slide past the first sits in a box the browser measures as 1px until its image
      // loads, and a lazy image in a 1px box may never load. Inside a carousel, load now.
      car.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });

      var per = Math.max(1, Math.round(parseFloat(
        getComputedStyle(track).getPropertyValue('--replo-carousel-slides-per-page')) || 1));
      var i = 0;
      var max = Math.max(0, slides.length - per);

      // Replo left the testimonial rail's slides with no width: its runtime wrote the
      // calc() in. The gallery rails already carry it in the server HTML, so only size the
      // ones that are missing it, and re-size when the breakpoint changes the slide count.
      function sizeSlides() {
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var mobile = window.matchMedia('(max-width: 640px)').matches;
        slides.forEach(function (s) {
          if (s.dataset.rfHadWidth !== '0' && s.style.width) return;
          s.dataset.rfHadWidth = '0';
          // The UGC rail is 3-up on mobile and a fixed 250px card on wider screens — the
          // widths Replo's runtime used. Stretching those 9:16 videos to the full column
          // leaves one tiny video in a sea of white, which is what it did before this.
          s.style.width = (!mobile && track.hasAttribute('data-rf-spp-mobile'))
            ? '250px'
            : 'calc((100% + ' + gap + 'px) / ' + per + ' - ' + gap + 'px)';
        });
      }

      function setBtn(b, off, disabled) {
        if (!b) return;
        b.disabled = disabled;
        if (off) b.classList.toggle(off, disabled);
      }
      function paint(animate) {
        if (!slides.length) { setBtn(prev, prevOff, true); setBtn(next, nextOff, true); return; }
        sizeSlides();
        var w = slides[0].getBoundingClientRect().width;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        // Fixed-width cards: how many fit is a measurement, not the CSS variable.
        var viewport = (track.parentElement || track).getBoundingClientRect().width;
        var fit = Math.max(1, Math.floor((viewport + gap) / (w + gap)));
        max = Math.max(0, slides.length - fit);
        i = Math.min(i, max);
        track.style.transitionProperty = animate ? 'transform' : 'none';
        track.style.transitionDuration = animate ? '350ms' : '0ms';
        track.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)';
        track.style.transform = 'translate3d(' + (-(w + gap) * i) + 'px, 0px, 0px)';
        slides.forEach(function (s, n) {
          if (n === i) s.setAttribute('data-is-active', 'true');
          else s.removeAttribute('data-is-active');
        });
        // Everything fits: centre the rail and retire the arrows rather than leave a
        // three-card row stranded against one edge of a wide column.
        var allFit = fit >= slides.length;
        track.style.justifyContent = allFit ? 'center' : '';
        track.style.width = allFit ? '100%' : '';
        if (prev) prev.style.display = allFit ? 'none' : '';
        if (next) next.style.display = allFit ? 'none' : '';
        if (dots) {
          dots.style.display = allFit ? 'none' : '';
          if (dotOn) [].forEach.call(dots.children, function (d, n) { d.classList.toggle(dotOn, n === i); });
        }
        setBtn(prev, prevOff, allFit || i <= 0);
        setBtn(next, nextOff, allFit || i >= max);
      }
      function go(n) { i = Math.min(max, Math.max(0, n)); paint(true); }

      if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); go(i - 1); });
      if (next) next.addEventListener('click', function (e) { e.preventDefault(); go(i + 1); });

      var x0 = null;
      track.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) < 40) return;
        go(dx < 0 ? i + 1 : i - 1);   // the track is dir="ltr", so left-swipe advances
      }, { passive: true });

      var t;
      window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          applyPerPage();
          per = Math.max(1, Math.round(parseFloat(
            getComputedStyle(track).getPropertyValue('--replo-carousel-slides-per-page')) || 1));
          max = Math.max(0, slides.length - per);
          i = Math.min(i, max);
          paint(false);
        }, 120);
      });
      paint(false);
    });
  }

  /* ---------- scroll reveal ------------------------------------------------ */
  /*  Replo ships its fade-in elements as `animation-play-state: paused` with
   *  `--initial-opacity: 0` and `animation-fill-mode: both`, i.e. INVISIBLE, and the hosted
   *  runtime flipped them to `running` as they scrolled into view. Without that the bundle
   *  cards and several sections render at opacity 0 — the page looks like it lost content.  */
  function initReveals(root) {
    var paused = [].slice.call(root.querySelectorAll('[data-rid]')).filter(function (n) {
      var s = getComputedStyle(n);
      return s.animationName !== 'none' && s.animationPlayState === 'paused';
    });
    if (!paused.length) return;
    function run(n) { n.style.animationPlayState = 'running'; }
    if (!('IntersectionObserver' in window)) { paused.forEach(run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { run(e.target); io.unobserve(e.target); }
      });
    }, { rootMargin: '300px 0px' });
    paused.forEach(function (n) { io.observe(n); });
    // Belt and braces: nothing on this page may stay invisible, whatever the observer does.
    setTimeout(function () { paused.forEach(run); io.disconnect(); }, 6000);
  }

  /* ---------- videos ------------------------------------------------------- */
  /*  Replo's hosted runtime owned the play button on every video tile. Without it the
   *  <video> elements just sat there: nothing happened on tap, and on iOS a video that has
   *  never played paints nothing at all, so the UGC rail rendered as three play icons on
   *  white. The builder gives every video a poster frame; this wires the button.  */
  function initVideos(root) {
    [].slice.call(root.querySelectorAll('video')).forEach(function (v) {
      if (v.dataset.rfVideo) return;
      v.dataset.rfVideo = '1';
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
      if (!v.getAttribute('src') && !v.querySelector('source')) return;

      // the play chevron Replo renders as a sibling overlay next to the video
      var box = v.parentElement, icon = null;
      for (var i = 0; i < 4 && box && !icon; i++) {
        icon = box.querySelector('svg[aria-label="Play"]');
        if (!icon) box = box.parentElement;
      }
      var overlay = icon && (icon.closest('[data-rid]') || icon.parentElement);
      if (overlay) overlay.style.cursor = 'pointer';
      var hit = box || v.parentElement;

      function paint() {
        if (!overlay) return;
        var playing = !v.paused && !v.ended;
        overlay.style.opacity = playing ? '0' : '';
        overlay.style.pointerEvents = playing ? 'none' : '';
        overlay.style.transition = 'opacity .2s';
      }
      v.addEventListener('play', paint);
      v.addEventListener('pause', paint);
      v.addEventListener('ended', paint);

      // an autoplaying hero stays muted and looping; a tile the customer taps gets its sound
      if (v.hasAttribute('autoplay')) { v.loop = true; paint(); return; }

      hit.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('a,button,[data-replo-add-product-variant-to-cart]')) return;
        if (v.paused || v.ended) {
          // one video at a time
          [].slice.call(document.querySelectorAll('video')).forEach(function (o) {
            if (o !== v && !o.paused && !o.hasAttribute('autoplay')) o.pause();
          });
          v.muted = false;
          var pr = v.play();
          if (pr && pr.catch) pr.catch(function () { v.muted = true; v.play(); });
        } else {
          v.pause();
        }
      });
      paint();
    });
  }

  /* ---------- bundle cards + add to cart ----------------------------------- */
  function initBuyBox(root) {
    var btn = root.querySelector('[data-replo-add-product-variant-to-cart]');
    var ladder = root.querySelector('.bl-root');

    // The page's bundle picker is the store's bundle-ladder snippet; it publishes the chosen
    // tier on its own root, and Replo's green button just carries out whatever it says.
    if (ladder && btn) {
      addToCart(btn, function () {
        var line = { id: parseInt(ladder.dataset.blVariant, 10),
                     quantity: parseInt(ladder.dataset.blQty, 10) || 1 };
        // the ladder also owns the purchase type (one-time vs subscription)
        var plan = parseInt(ladder.dataset.blSellingPlan, 10);
        if (plan) line.selling_plan = plan;
        return line;
      });
      return;
    }

    var cards = [].slice.call(root.querySelectorAll('[data-rf-variant]'));
    if (!cards.length) return;
    var input = root.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    var chosen = null;

    function select(card) {
      chosen = card;
      cards.forEach(function (c) {
        var on = c === card;
        var opt = c.querySelector('[role="option"]');
        var box = c.querySelector('[data-replo-variant-select]');
        if (opt) opt.setAttribute('aria-selected', on ? 'true' : 'false');
        if (box && c.dataset.rfSelClass) box.classList.toggle(c.dataset.rfSelClass, on);
      });
      if (input) input.value = card.dataset.rfVariant;
    }

    cards.forEach(function (c) {
      c.addEventListener('click', function () { select(c); });
      var opt = c.querySelector('[role="option"]');
      if (opt) opt.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); select(c); }
      });
    });
    select(cards.filter(function (c) { return c.hasAttribute('data-rf-preselect'); })[0] || cards[0]);

    if (!btn) return;
    addToCart(btn, function () {
      return chosen ? { id: parseInt(chosen.dataset.rfVariant, 10), quantity: 1 } : null;
    });
  }

  function addToCart(btn, pick) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var line = pick();
      if (!line || !line.id || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [line] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        btn.removeAttribute('aria-busy');
        // UpCart is the store's one cart drawer (see snippets/one-cart.liquid).
        if (typeof window.upcartRefreshCart === 'function' && typeof window.upcartOpenCart === 'function') {
          Promise.resolve(window.upcartRefreshCart())
            .then(function () { window.upcartOpenCart(); })
            .catch(function () { window.upcartOpenCart(); });
          return;
        }
        window.location.href = '/cart';
      }).catch(function () {
        btn.removeAttribute('aria-busy');
      });
    });
  }

  function boot() {
    var root = document.getElementById('replo-fullpage-element') || document;
    initReveals(root);
    initCollapsibles(root);
    initCarousels(root);
    initVideos(root);
    initBuyBox(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
